import { useQuery } from '@apollo/client';
import {
  IAttributesResponse,
  WorkspaceDataSource,
  WorkspaceRecordsPage,
  WorkspaceRecordsRequest,
  WorkspaceRecordsVariables,
} from '@xometry/workspace';
import { QueryName } from 'components/Workspaces/collections';
import { useMemo, useRef } from 'react';
import useAuthorizedQuery from 'utils/hooks/useAuthorizedQuery';
import { useExternalDownload } from 'utils/hooks/useDownload';

import { attributesQuery, objectQuery, ObjectQueryResponse } from './erpWorkspaceQueries';
import useLazyExport from './useLazyExport';

const toVariables = (request: WorkspaceRecordsRequest): WorkspaceRecordsVariables => ({
  timezoneOffset: request.timezoneOffset,
  filterQuery: request.filterQuery,
  queryOptions: request.queryOptions,
  sortParams: request.sortParams,
  paginationParams: request.paginationParams,
  presetId: request.presetId,
});

/** ERP's Workspace backend: GraphQL introspection + `workspaces*` queries through Apollo. */
export const erpWorkspaceDataSource: WorkspaceDataSource = {
  useSchema: (queryName) => {
    const query = useMemo(() => attributesQuery(queryName), [queryName]);
    const { data, loading, error } = useQuery<IAttributesResponse>(query);

    return { fields: data?.__type.fields, loading, error };
  },

  useRecords: (request, { skip, onCompleted, onError }) => {
    const queryName = request.queryName;
    const query = objectQuery(queryName, request.fields);

    const { authorized, serverMessage, data, loading, refetch, error } = useAuthorizedQuery<ObjectQueryResponse>(
      query,
      {
        skip,
        // Frameworks change outside the workspace, so they are never read from the cache.
        fetchPolicy: queryName === QueryName.F ? 'no-cache' : 'network-only',
        notifyOnNetworkStatusChange: true,
        variables: toVariables(request),
        onCompleted,
        onError,
      },
    );

    return {
      data: data?.[queryName] as WorkspaceRecordsPage | undefined,
      loading,
      // A 403 also comes as an Apollo error; the grid must show its "not authorized" state, not the error.
      error: authorized === false ? undefined : error,
      authorized: authorized ?? true,
      serverMessage,
      refetch: (next) => refetch(toVariables(next)),
    };
  },

  useExport: (queryName) => {
    const download = useExternalDownload();
    const fileName = useRef('');
    const [loadFile, loading] = useLazyExport({
      queryName: queryName,
      onCompleted: (url: string) => download(url, fileName.current),
    });

    return {
      exportRecords: async (params) => {
        fileName.current = `${params.filename}.${params.format}`;
        await loadFile(params);
      },
      loading,
    };
  },
};
